import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportTourOperatorLogo from './ExportTourOperatorLogo';
import { PAGE_WIDTH_PX } from './ExportPage';
import { KoobLoader } from '@koob/ui';

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

export default function ExportNav({
  tourOperator,
  sections,
  onPrint,
  printLabel,
  processing,
}) {
  const [activeId, setActiveId] = useState(null);
  const observerRef = useRef(null);
  const ownerRef = useRef(new Map());
  const buttonRefs = useRef(new Map());

  useEffect(() => {
    if (!activeId) return;
    buttonRefs.current.get(activeId)?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [activeId]);

  useEffect(() => {
    const visibleRatios = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibleRatios.set(
            entry.target.id,
            entry.isIntersecting ? entry.intersectionRatio : 0,
          );
        });

        let bestId = null;
        let bestRatio = 0;
        visibleRatios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        setActiveId(
          bestRatio > 0 ? (ownerRef.current.get(bestId) ?? bestId) : null,
        );
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-45% 0px -45% 0px' },
    );
    observerRef.current = observer;

    const ownerOf = new Map();
    sections.forEach(({ id, covers = [] }) => {
      [id, ...covers].forEach((observedId) => {
        const el = document.getElementById(observedId);
        if (!el) return;
        ownerOf.set(observedId, id);
        observer.observe(el);
      });
    });
    ownerRef.current = ownerOf;

    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="export-hidden sticky top-0 z-navbar bg-[var(--td-paper)] border-b border-k-gray/40">
      <div
        className="mx-auto px-6 py-3 flex items-center gap-8"
        style={{ maxWidth: `${PAGE_WIDTH_PX}px` }}
      >
        <button
          type="button"
          onClick={() => scrollToId('section-cover')}
          className="appearance-none bg-transparent border-0 p-0 m-0 outline-none flex-shrink-0 cursor-pointer"
        >
          <ExportTourOperatorLogo tourOperator={tourOperator} compact />
        </button>

        <nav className="export-nav-scroll flex-1 flex items-center gap-6 overflow-x-auto text-2xs font-bold tracking-widest">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              ref={(el) => {
                if (el) buttonRefs.current.set(section.id, el);
                else buttonRefs.current.delete(section.id);
              }}
              onClick={() => scrollToId(section.id)}
              className={[
                'appearance-none bg-transparent border-0 p-0 m-0 outline-none cursor-pointer whitespace-nowrap uppercase transition-colors',
                activeId === section.id
                  ? 'text-[var(--td-primary)]'
                  : 'text-[var(--td-ink-muted)] hover:text-[var(--td-ink)]',
              ].join(' ')}
            >
              {section.label}
            </button>
          ))}
        </nav>

        <PrintButton
          onClick={onPrint}
          label={printLabel}
          processing={processing}
        />
      </div>
    </div>
  );
}

function PrintButton({ onClick, label, processing }) {
  const { t } = useTranslation('trips');

  return (
    <button
      type="button"
      onClick={() => !processing && onClick()}
      disabled={processing}
      className="appearance-none outline-none flex-shrink-0 bg-[var(--td-primary)] hover:bg-[var(--td-primary-strong)] text-white text-2xs font-bold tracking-widest uppercase px-3 py-1.5 rounded border-0 cursor-pointer"
    >
      {processing ? (
        <KoobLoader size={16} color="currentColor" />
      ) : (
        label || t('export.nav.printPdf')
      )}
    </button>
  );
}
